import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { DesignSystemToken, TokenCategory } from 'designSystem/types';
import { TokenCard } from './tokens/TokenCard';
import FilterPills from './FilterPills';
import SearchBar from './SearchBar';
import * as S from './styles';
import * as TokenS from './tokens/styles';

// Map TokenCategory to display name
const categoryDisplayNames: Record<TokenCategory, string> = {
	color: 'Colors',
	typography: 'Typography',
	spacing: 'Spacing',
	elevation: 'Elevation',
	borderRadius: 'Border Radius',
	opacity: 'Opacity',
	motion: 'Motion',
	other: 'Other',
};

const TokensView = () => {
	const [selectedTokenId, setSelectedTokenId] = useState<string | null>(null);
	const designSystemContent = useSelector((r: RootReducer) => r.mode.designSystemContent);
	const { selectedCategories, searchQuery } = useSelector(
		(r: RootReducer) => r.mode.designSystemFilters.tokens
	);

	// Get available categories with counts
	const availableCategories = useMemo(() => {
		if (!designSystemContent?.tokens) return [];
		return (Object.keys(categoryDisplayNames) as TokenCategory[]).map((category) => ({
			value: category,
			label: categoryDisplayNames[category],
			count: designSystemContent.tokens!.byCategory[category]?.length || 0,
		}));
	}, [designSystemContent?.tokens]);

	// Filter tokens based on selected categories and search query
	const tokensToDisplay = useMemo(() => {
		if (!designSystemContent?.tokens) return [];

		let tokens: DesignSystemToken[] = [];

		// If categories selected, show union of all selected categories
		if (selectedCategories.length > 0) {
			const tokenSets = selectedCategories.map(
				(cat) => designSystemContent.tokens!.byCategory[cat] || []
			);
			// Union: combine all sets and remove duplicates
			const tokenMap = new Map<string, DesignSystemToken>();
			tokenSets.forEach((tokenSet) => {
				tokenSet.forEach((token) => {
					tokenMap.set(token.id, token);
				});
			});
			tokens = Array.from(tokenMap.values());
		} else {
			// No categories selected, show all
			tokens = designSystemContent.tokens.all || [];
		}

		// Apply search filter
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			tokens = tokens.filter(
				(token) =>
					token.name.toLowerCase().includes(query) ||
					token.id.toLowerCase().includes(query) ||
					token.cssVariable.toLowerCase().includes(query)
			);
		}

		return tokens;
	}, [designSystemContent?.tokens, selectedCategories, searchQuery]);

	return (
		<S.SectionBase>
			<S.SectionHeader>
				<S.SectionTitle>Tokens</S.SectionTitle>
				<SearchBar section="tokens" placeholder="Search tokens..." />
			</S.SectionHeader>
			<S.SectionContent>
				<FilterPills section="tokens" categories={availableCategories} />
				{tokensToDisplay.length === 0 ? (
					<p>
						No tokens found
						{selectedCategories.length > 0 && ` matching selected categories`}
						{searchQuery && ` matching "${searchQuery}"`}.
					</p>
				) : (
					<TokenS.TokenGrid>
						{tokensToDisplay.map((token) => (
							<TokenCard
								key={token.id}
								token={token}
								selected={selectedTokenId === token.id}
								onClick={() => setSelectedTokenId(token.id === selectedTokenId ? null : token.id)}
							/>
						))}
					</TokenS.TokenGrid>
				)}
			</S.SectionContent>
		</S.SectionBase>
	);
};

export default TokensView;
